import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Icon } from "@shopify/polaris";
import {
  PackageIcon,
  PersonIcon,
  StoreIcon,
  FileIcon,
  PersonAddIcon,
  AirplaneIcon,
  CalendarIcon,
  LinkIcon,
} from "@shopify/polaris-icons";
import {
  FileText,
  FileSpreadsheet,
  File,
  Image as ImageIconLucide,
  FileArchive,
} from "lucide-react";
import {
  parseMessageContent,
  AppLinkKind,
  ChatContentPart,
} from "./parse-chat-content";

import { ChatMessage } from "types/Chat";
import { Spinner } from "@shopify/polaris";
import { API_URL } from "../../constants";
import { getMimeFromExtension, isChatAttachmentImage } from "./chat-attachment-utils";

interface Props {
  message: ChatMessage;
  companyId: string;
  /** Render variant — affects the chip background to remain readable. */
  mine?: boolean;
}

const KIND_ICON: Record<AppLinkKind, React.FunctionComponent<React.SVGProps<SVGSVGElement>>> = {
  asset: PackageIcon,
  profile: PersonIcon,
  customer: StoreIcon,
  document: FileIcon,
  onboarding: PersonAddIcon,
  business_trip: AirplaneIcon,
  leave: CalendarIcon,
  unknown: LinkIcon,
};

const getIconForMime = (filename: string) => {
  const mime = getMimeFromExtension(filename);
  if (mime.startsWith("image/")) return ImageIconLucide;
  if (mime === "application/pdf") return FileText;
  if (
    mime.includes("spreadsheet") ||
    mime.includes("excel") ||
    mime === "text/csv"
  )
    return FileSpreadsheet;
  if (mime.includes("word") || mime.includes("officedocument.wordprocessingml"))
    return FileText;
  if (mime.includes("zip") || mime.includes("rar")) return FileArchive;
  return File;
};

const KIND_I18N: Record<AppLinkKind, string> = {
  asset: "link_kind_asset",
  profile: "link_kind_profile",
  customer: "link_kind_customer",
  document: "link_kind_document",
  onboarding: "link_kind_onboarding",
  business_trip: "link_kind_business_trip",
  leave: "link_kind_leave",
  unknown: "link_kind_link",
};

const ChatMessageContent: React.FC<Props> = ({ message, mine, companyId }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { content, attachment, localPreviewUrl, uploadStatus } = message;
  const [imgLoaded, setImgLoaded] = React.useState(false);

  const parts: ChatContentPart[] = useMemo(
    () => parseMessageContent(content, window.location.origin),
    [content]
  );

  const renderAttachment = () => {
    if (!attachment && !localPreviewUrl) return null;

    const displayName = attachment?.title || attachment?.name || "File";
    const isImage = isChatAttachmentImage(displayName, localPreviewUrl);

    const fileCompanyId = attachment?.companyId || companyId;
    const LucideIcon = getIconForMime(displayName);

    // Fallback URL for files already on the server
    const downloadUrl = attachment?.id
      ? `${API_URL}/${fileCompanyId}/files/download/${attachment.id}?mode=download`
      : "#";

    return (
      <div className={`${content ? "mt-2 " : ""}flex flex-col gap-1 w-full max-w-[220px] overflow-hidden`}>
        {isImage ? (
          <div className={`relative rounded-md overflow-hidden flex items-center justify-center transition-all duration-300 ${!imgLoaded ? "bg-white/80 backdrop-blur-xl w-[180px] h-[150px]" : "w-auto h-auto"}`}>
            
            {/* White Blur Placeholder (Visible only during load) */}
            {!imgLoaded && (
              <div className="absolute inset-0 z-20 flex items-center justify-center">
                <Spinner size="small" />
              </div>
            )}

            <img
              src={localPreviewUrl || `${API_URL}/${fileCompanyId}/files/download/${attachment?.id}?mode=view`}
              alt={displayName}
              className={`relative z-10 max-w-full max-h-[450px] object-contain cursor-pointer hover:opacity-95 transition-opacity duration-500 ${imgLoaded ? "opacity-100" : "opacity-0"}`}
              onLoad={() => setImgLoaded(true)}
              onError={() => setImgLoaded(true)}
              onClick={() => {
                if (attachment?.id) {
                  window.open(`${API_URL}/${fileCompanyId}/files/download/${attachment.id}?mode=view`, "_blank");
                }
              }}
            />
            
            {uploadStatus === "uploading" && (
              <div className="absolute inset-0 z-30 bg-black/20 flex items-center justify-center backdrop-blur-[1px]">
                <Spinner size="small" />
              </div>
            )}
          </div>
        ) : (
          <a
            href={downloadUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={
              "flex items-center gap-3 p-2.5 rounded-lg border transition-all duration-200 group/file w-[220px] " +
              (mine
                ? "bg-white/10 border-white/10 hover:bg-white/20 text-white"
                : "bg-black/20 border-white/5 hover:bg-black/30 text-gray-100")
            }
            onClick={(e) => {
              if (uploadStatus === "uploading") e.preventDefault();
            }}
          >
            <div className={`p-2 rounded-md flex-shrink-0 ${mine ? "bg-white/10" : "bg-black/20 text-gray-400"}`}>
              <LucideIcon size={20} className={mine ? "text-white" : "text-gray-300"} />
            </div>
            <div className="min-w-0 flex-1 flex flex-col gap-0.5">
              <span className={`text-[13px] font-semibold truncate leading-tight ${mine ? "text-white" : "text-gray-100"}`} title={displayName}>
                {displayName}
              </span>
              <div className={`text-[10px] flex items-center gap-1.5 opacity-70 ${mine ? "text-blue-100" : "text-gray-400"}`}>
                <span className="uppercase tracking-wider">
                  {displayName.split(".").pop()?.toUpperCase() || "FILE"}
                </span>
              </div>
            </div>
            {uploadStatus === "uploading" && <Spinner size="small" />}
          </a>
        )}
      </div>
    );
  };

  return (
    <>
      {content && (
        <div className={parts.length === 1 && parts[0].type === "text" ? "" : "flex flex-wrap items-center"}>
          {parts.map((part, idx) => {
            if (part.type === "text") return <span key={idx}>{part.value}</span>;
            if (part.type === "url") {
              return (
                <a
                  key={idx}
                  href={part.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={
                    "underline break-all " +
                    (mine ? "text-blue-100 hover:text-white" : "text-blue-300 hover:text-blue-200")
                  }
                  onClick={(e) => e.stopPropagation()}
                >
                  {part.url}
                </a>
              );
            }
            // app_link — render compact chip with in-app navigation
            const IconComp = KIND_ICON[part.kind];
            const label = t(KIND_I18N[part.kind], {
              defaultValue: part.kind.replace("_", " "),
            });
            return (
              <button
                key={idx}
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  navigate(part.route);
                }}
                title={part.url}
                className={
                  "inline-flex items-center gap-1 px-1.5 py-0.5 mx-0.5 rounded text-xs font-medium align-baseline " +
                  (mine
                    ? "bg-blue-500/40 hover:bg-blue-500/60 text-white"
                    : "bg-gray-700 hover:bg-gray-600 text-blue-200")
                }
              >
                <span className="block w-3.5 h-3.5">
                  <Icon source={IconComp as any} tone="inherit" />
                </span>
                {label}
              </button>
            );
          })}
        </div>
      )}
      {renderAttachment()}
    </>
  );
};

export default ChatMessageContent;
